/**
 * One-time self-heal for "fossil" service workers.
 *
 * PanLuma deliberately ships NO service worker on web — offline is data-only
 * via IndexedDB + React Query persistence (see docs/platform/offline-design.md).
 * But old builds once registered a precaching SW, and a precaching SW is the
 * most aggressive cache there is: a browser still carrying one would serve a
 * stale app shell indefinitely ("stuck on an old version / clear your cache").
 *
 * On boot we unregister any SW registered for this origin and delete its Cache
 * Storage entries. This runs every load but is a near-no-op once clean (one
 * `getRegistrations()` call), so there is no flag to manage.
 */
export async function cleanupFossilServiceWorkers(): Promise<void> {
  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      if (registrations.length > 0) {
        await Promise.all(registrations.map((registration) => registration.unregister()));
        console.info(`Unregistered ${registrations.length} fossil service worker(s).`);
      }
    } catch (error) {
      console.error('Failed to unregister fossil service workers:', error);
    }
  }

  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      if (cacheNames.length > 0) {
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
        console.info(`Deleted ${cacheNames.length} stale Cache Storage entry(ies).`);
      }
    } catch (error) {
      console.error('Failed to delete fossil Cache Storage:', error);
    }
  }
}
